import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { StObservationItem } from '@/modules/shared/components/observation-item/ObservationItem'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * MedicationList
 * ------------------------------------------------------------------------------------------------- */

type MedicationListRootProps = ComponentPropsWithoutRef<'div'>

const MedicationListRoot = ({ children, ...props }: MedicationListRootProps) => <StRoot {...props}>{children}</StRoot>

/* ------------------------------------------------------------------------------------------------- *
 * MedicationList.Item
 * ------------------------------------------------------------------------------------------------- */

type MedicationListItemProps = ComponentPropsWithoutRef<typeof Span>

/** One medication line; its parts stack in the order they are composed. */
const MedicationListItem = ({ children, ...props }: MedicationListItemProps) => (
  <StItem type='BodyMediumRegular' {...props}>
    {children}
  </StItem>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationList.Name
 * ------------------------------------------------------------------------------------------------- */

type MedicationListNameProps = ComponentPropsWithoutRef<typeof Span>

const MedicationListName = ({ children, ...props }: MedicationListNameProps) => (
  <StName type='BodyMediumRegular' {...props}>
    {children}
  </StName>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationList.Dosage
 * ------------------------------------------------------------------------------------------------- */

type MedicationListDosageProps = ComponentPropsWithoutRef<typeof Span>

const MedicationListDosage = ({ children, ...props }: MedicationListDosageProps) => (
  <StDosage type='BodyMediumRegular' {...props}>
    {children}
  </StDosage>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const MedicationList = Object.assign(MedicationListRoot, {
  Item: MedicationListItem,
  Name: MedicationListName,
  Dosage: MedicationListDosage,
})

export { MedicationList }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
`

const StItem = styled(StObservationItem)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
`

const StName = styled(Span)`
  font-weight: 500;
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`

const StDosage = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`
